import { useCallback, useEffect, useRef, type RefObject } from 'react';

interface UseAutoScrollResult {
  scrollRef: RefObject<HTMLDivElement | null>;
  bottomRef: RefObject<HTMLDivElement | null>;
  scrollToBottom: () => void;
}

const STICKY_THRESHOLD_PX = 80;

export function useAutoScroll(dependency: unknown): UseAutoScrollResult {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const stuckToBottomRef = useRef(true);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const recompute = () => {
      const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
      stuckToBottomRef.current = distance <= STICKY_THRESHOLD_PX;
    };
    node.addEventListener('scroll', recompute, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recompute) : null;
    ro?.observe(node);
    return () => {
      node.removeEventListener('scroll', recompute);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!stuckToBottomRef.current) return;
    pinToBottom(scrollRef.current);
  }, [dependency]);

  const scrollToBottom = useCallback(() => {
    stuckToBottomRef.current = true;
    pinToBottom(scrollRef.current);
  }, []);

  return { scrollRef, bottomRef, scrollToBottom };
}

function pinToBottom(node: HTMLDivElement | null): void {
  if (!node) return;
  node.scrollTop = node.scrollHeight;
}
